// ==============================================
// The boat — dimensions, and how they read against a shared passage.
//
// Draft is the safety-critical number in this domain: a pass a 1.0 m cat clears
// will put a 2.4 m monohull on the coral. The card exists to get it declared
// once, and the badge exists to report what a passage does and does not tell
// you about your own boat.
//
// See docs/core/architecture/NAV_VESSEL_PROFILE.md.
// ==============================================

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { DraftFit, NavElement, VesselProfile } from '../../types/nav';

const TYPE_LABELS: Array<{ value: string; label: string }> = [
  { value: 'mono', label: 'Monohull' },
  { value: 'cat', label: 'Catamaran' },
  { value: 'tri', label: 'Trimaran' },
  { value: 'power', label: 'Power' },
  { value: 'motorsailer', label: 'Motorsailer' },
];

const PROPULSION_LABELS: Array<{ value: string; label: string }> = [
  { value: 'sail', label: 'Sail' },
  { value: 'power', label: 'Power' },
  { value: 'both', label: 'Both' },
];

export const metres = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(2)} m`);

/** Feet, for skippers who think in them. Shown alongside, never instead. */
const feet = (v: number | null | undefined) => (v == null ? null : `${(v * 3.28084).toFixed(1)} ft`);

export function useVessels() {
  return useQuery({ queryKey: ['vessels'], queryFn: () => api.vesselList() });
}

/** The boat we compare against: the single boat the user owns. */
export function primaryVessel(vessels: VesselProfile[] | undefined): VesselProfile | null {
  if (!vessels?.length) return null;
  const owned = vessels.filter((v) => v.role === 'owner');
  return owned.length === 1 ? owned[0] : null;
}

// ---------------------------------------------------------------- badge

// Colour never carries the meaning alone — glyph and words do too, for a sunlit
// cockpit screen and for a colourblind skipper.
const FIT_STYLE: Record<DraftFit['verdict'], { ring: string; text: string; glyph: string }> = {
  deeper_sailed: { ring: 'border-emerald-500/50 bg-emerald-500/10', text: 'text-emerald-200', glyph: '✓' },
  shallower_only: { ring: 'border-amber-500/50 bg-amber-500/10', text: 'text-amber-200', glyph: '~' },
  unknown: { ring: 'border-slate-600 bg-slate-800/60', text: 'text-slate-300', glyph: '?' },
};

export function DraftFitBadge({ fit, compact = false }: { fit: DraftFit; compact?: boolean }) {
  const style = FIT_STYLE[fit.verdict];
  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${style.ring} ${style.text}`}>
        <span aria-hidden>{style.glyph}</span>{fit.headline}
      </span>
    );
  }
  return (
    <div className={`mb-3 rounded-md border px-2.5 py-2 ${style.ring}`}>
      <p className={`flex items-start gap-1.5 text-[11px] font-semibold ${style.text}`}>
        <span aria-hidden className="font-mono">{style.glyph}</span>
        <span>{fit.headline}</span>
      </p>
      <p className="mt-1 text-[11px] leading-relaxed text-slate-300">{fit.evidence}</p>
      {fit.airDraftWarning && (
        <p className="mt-1.5 border-t border-white/10 pt-1.5 text-[11px] leading-relaxed text-amber-200">
          <span aria-hidden className="font-mono">!</span> {fit.airDraftWarning}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- card

interface Draft {
  name: string; vesselType: string; propulsion: string;
  loaM: string; beamM: string; draftM: string; airDraftM: string;
}

const toDraft = (v: VesselProfile | null): Draft => ({
  name: v?.name ?? '',
  vesselType: v?.vesselType ?? '',
  propulsion: v?.propulsion ?? '',
  loaM: v?.loaM != null ? String(v.loaM) : '',
  beamM: v?.beamM != null ? String(v.beamM) : '',
  draftM: v?.draftM != null ? String(v.draftM) : '',
  airDraftM: v?.airDraftM != null ? String(v.airDraftM) : '',
});

const numOrNull = (s: string): number | null => {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

function Field({ label, hint, value, onChange, placeholder }: {
  label: string; hint: string | null; value: string; placeholder: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-slate-300">{label}</span>
      <input
        type="number" inputMode="decimal" step="0.01" min="0"
        value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white placeholder:text-slate-600"
      />
      <span className="mt-0.5 block h-3 text-[10px] tabular-nums text-slate-500">{hint ?? ''}</span>
    </label>
  );
}

/**
 * Declare the boat once.
 *
 * The card pays off inside itself: the moment a draft is typed it says what
 * that draft means for the passages already on this page. That sentence is the
 * whole argument for filling the form in, and without it this is just another
 * settings screen nobody visits.
 */
export function YourBoatCard({ shared, onClose }: { shared: NavElement[]; onClose: () => void }) {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const { data: vessels, isLoading } = useVessels();
  const vessel = primaryVessel(vessels);

  const [draft, setDraft] = useState<Draft>(() => toDraft(null));
  const [newName, setNewName] = useState('');
  useEffect(() => { if (vessel) setDraft(toDraft(vessel)); }, [vessel?.id]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['vessels'] });
    void queryClient.invalidateQueries({ queryKey: ['nav-list'] });
  };

  const create = useMutation({
    mutationFn: (name: string) => api.vesselCreate(name),
    onSuccess: () => { invalidate(); addToast({ type: 'success', title: 'Boat added' }); },
  });

  const save = useMutation({
    mutationFn: (id: string) => api.vesselSetDimensions(id, {
      vessel_type: draft.vesselType || null,
      propulsion: draft.propulsion || null,
      loa_m: numOrNull(draft.loaM),
      beam_m: numOrNull(draft.beamM),
      draft_m: numOrNull(draft.draftM),
      air_draft_m: numOrNull(draft.airDraftM),
    }),
    onSuccess: () => {
      invalidate();
      addToast({ type: 'success', title: 'Saved', message: 'Shared passages now compare against your boat.' });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      addToast({ type: 'error', title: 'Check the dimensions', message: msg ?? 'Could not save your boat' });
    },
  });

  // The payoff, live: what this draft means for what is already on the page.
  const typedDraft = numOrNull(draft.draftM);
  const payoff = useMemo(() => {
    if (typedDraft == null) return null;
    const withDepth = shared.filter((e) => e.draftMSnapshot != null);
    if (!withDepth.length) return null;
    const within = withDepth.filter((e) => (e.draftMSnapshot as number) >= typedDraft).length;
    return { within, total: withDepth.length };
  }, [typedDraft, shared]);

  if (isLoading) return <div className="p-6 text-center text-sm text-slate-400">Loading your boat…</div>;

  if (!vessel) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-300">
          {vessels?.length
            ? 'You are aboard more than one boat, so we will not guess which one to compare against. Add the boat you sail, or set its dimensions in Ship’s Papers.'
            : 'Add your boat once. Every passage you share then carries what sailed it, and shared passages tell you how they compare with your own draft.'}
        </p>
        <div className="flex gap-2">
          <input
            value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="Boat name" maxLength={120}
            className="flex-1 rounded-md border border-slate-600 bg-slate-800 px-2 py-1.5 text-sm text-white placeholder:text-slate-600"
          />
          <button
            type="button" disabled={!newName.trim() || create.isPending}
            onClick={() => create.mutate(newName.trim())}
            className="rounded-md bg-amber-400 px-3 py-1.5 text-sm font-bold text-slate-900 disabled:opacity-40"
          >{create.isPending ? 'Adding…' : 'Add boat'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-white">{vessel.name}</p>
        <p className="text-[11px] text-slate-400">
          Dimensions in metres. Draft is the one that matters most — it decides which shared passages mean anything for your boat.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="block text-[11px] font-medium text-slate-300">Type</span>
          <select
            value={draft.vesselType} onChange={(e) => setDraft({ ...draft, vesselType: e.target.value })}
            className="mt-0.5 w-full rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
          >
            <option value="">—</option>
            {TYPE_LABELS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <span className="mt-0.5 block h-3" />
        </label>
        <label className="block">
          <span className="block text-[11px] font-medium text-slate-300">Propulsion</span>
          <select
            value={draft.propulsion} onChange={(e) => setDraft({ ...draft, propulsion: e.target.value })}
            className="mt-0.5 w-full rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-white"
          >
            <option value="">—</option>
            {PROPULSION_LABELS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <span className="mt-0.5 block h-3" />
        </label>

        <Field label="Draft" placeholder="2.10" value={draft.draftM} hint={feet(numOrNull(draft.draftM))}
          onChange={(v) => setDraft({ ...draft, draftM: v })} />
        <Field label="Air draft" placeholder="18.50" value={draft.airDraftM} hint={feet(numOrNull(draft.airDraftM))}
          onChange={(v) => setDraft({ ...draft, airDraftM: v })} />
        <Field label="Length overall" placeholder="12.50" value={draft.loaM} hint={feet(numOrNull(draft.loaM))}
          onChange={(v) => setDraft({ ...draft, loaM: v })} />
        <Field label="Beam" placeholder="3.90" value={draft.beamM} hint={feet(numOrNull(draft.beamM))}
          onChange={(v) => setDraft({ ...draft, beamM: v })} />
      </div>

      {payoff && (
        <p className="rounded-md border-l-2 border-emerald-400 bg-emerald-400/10 px-2.5 py-2 text-[11px] leading-relaxed text-emerald-200">
          With a {typedDraft?.toFixed(2)} m draft, <b>{payoff.within} of {payoff.total}</b>{' '}
          shared {payoff.total === 1 ? 'passage' : 'passages'} here {payoff.within === 1 ? 'was' : 'were'} sailed by a boat
          drawing at least as much.
        </p>
      )}

      <p className="text-[11px] leading-relaxed text-slate-500">
        Recorded against everything you share from now on, as the numbers stand today. Refitting later never rewrites
        what an earlier passage says about itself.
      </p>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose}
          className="rounded-md border border-slate-600 px-3 py-1.5 text-sm text-white">Cancel</button>
        <button
          type="button" disabled={!vessel.canEdit || save.isPending}
          title={vessel.canEdit ? undefined : 'Only the owner or captain can change the dimensions'}
          onClick={() => save.mutate(vessel.id)}
          className="rounded-md bg-amber-400 px-3 py-1.5 text-sm font-bold text-slate-900 disabled:opacity-40"
        >{save.isPending ? 'Saving…' : 'Save boat'}</button>
      </div>
    </div>
  );
}
